import _extends from "../../../@babel/runtime/helpers/esm/extends.js";
import * as React from "react";
import { ariaHidden, ModalManager } from "./ModalManager.js";
import useForkRef from "../../utils/useForkRef/useForkRef.js";
import useEventCallback from "../../utils/useEventCallback/useEventCallback.js";
import { extractEventHandlers } from "../utils/extractEventHandlers.js";
import createChainedFunction from "../../utils/createChainedFunction/createChainedFunction.js";
import ownerDocument from "../../utils/ownerDocument/ownerDocument.js";
function getContainer(container) {
  return typeof container == "function" ? container() : container;
}
function getHasTransition(children) {
  return children ? children.props.hasOwnProperty("in") : !1;
}
const defaultManager = new ModalManager();
function useModal(parameters) {
  const {
    container,
    disableEscapeKeyDown = !1,
    disableScrollLock = !1,
    // @ts-ignore internal logic - Base UI supports the manager as a prop too
    manager = defaultManager,
    closeAfterTransition = !1,
    onTransitionEnter,
    onTransitionExited,
    children,
    onClose,
    open,
    rootRef
  } = parameters, modal = React.useRef({}), mountNodeRef = React.useRef(null), modalRef = React.useRef(null), handleRef = useForkRef(modalRef, rootRef), [exited, setExited] = React.useState(!open), hasTransition = getHasTransition(children);
  let ariaHiddenProp = !0;
  (parameters["aria-hidden"] === "false" || parameters["aria-hidden"] === !1) && (ariaHiddenProp = !1);
  const getDoc = () => ownerDocument(mountNodeRef.current), getModal = () => (modal.current.modalRef = modalRef.current, modal.current.mount = mountNodeRef.current, modal.current), handleMounted = () => {
    manager.mount(getModal(), {
      disableScrollLock
    }), modalRef.current && (modalRef.current.scrollTop = 0);
  }, handleOpen = useEventCallback(() => {
    const resolvedContainer = getContainer(container) || getDoc().body;
    manager.add(getModal(), resolvedContainer), modalRef.current && handleMounted();
  }), isTopModal = React.useCallback(() => manager.isTopModal(getModal()), [manager]), handlePortalRef = useEventCallback((node) => {
    mountNodeRef.current = node, node && (open && isTopModal() ? handleMounted() : modalRef.current && ariaHidden(modalRef.current, ariaHiddenProp));
  }), handleClose = React.useCallback(() => {
    manager.remove(getModal(), ariaHiddenProp);
  }, [ariaHiddenProp, manager]);
  React.useEffect(() => () => {
    handleClose();
  }, [handleClose]), React.useEffect(() => {
    open ? handleOpen() : (!hasTransition || !closeAfterTransition) && handleClose();
  }, [open, handleClose, hasTransition, closeAfterTransition, handleOpen]);
  const createHandleKeyDown = (otherHandlers) => (event) => {
    var _otherHandlers$onKeyD;
    (_otherHandlers$onKeyD = otherHandlers.onKeyDown) == null || _otherHandlers$onKeyD.call(otherHandlers, event), !(event.key !== "Escape" || event.which === 229 || // Wait until IME is settled.
    !isTopModal()) && (disableEscapeKeyDown || (event.stopPropagation(), onClose && onClose(event, "escapeKeyDown")));
  }, createHandleBackdropClick = (otherHandlers) => (event) => {
    var _otherHandlers$onClic;
    (_otherHandlers$onClic = otherHandlers.onClick) == null || _otherHandlers$onClic.call(otherHandlers, event), event.target === event.currentTarget && onClose && onClose(event, "backdropClick");
  };
  return {
    getRootProps: (otherHandlers = {}) => {
      const propsEventHandlers = extractEventHandlers(parameters);
      delete propsEventHandlers.onTransitionEnter, delete propsEventHandlers.onTransitionExited;
      const externalEventHandlers = _extends({}, propsEventHandlers, otherHandlers);
      return _extends({
        role: "presentation"
      }, externalEventHandlers, {
        onKeyDown: createHandleKeyDown(externalEventHandlers),
        ref: handleRef
      });
    },
    getBackdropProps: (otherHandlers = {}) => {
      const externalEventHandlers = otherHandlers;
      return _extends({
        "aria-hidden": !0
      }, externalEventHandlers, {
        onClick: createHandleBackdropClick(externalEventHandlers),
        open
      });
    },
    getTransitionProps: () => {
      const handleEnter = () => {
        setExited(!1), onTransitionEnter && onTransitionEnter();
      }, handleExited = () => {
        setExited(!0), onTransitionExited && onTransitionExited(), closeAfterTransition && handleClose();
      };
      return {
        onEnter: createChainedFunction(handleEnter, children == null ? void 0 : children.props.onEnter),
        onExited: createChainedFunction(handleExited, children == null ? void 0 : children.props.onExited)
      };
    },
    rootRef: handleRef,
    portalRef: handlePortalRef,
    isTopModal,
    exited,
    hasTransition
  };
}
export {
  useModal
};
//# sourceMappingURL=useModal.js.map
